/**
 * Analytics event / property / canonical-pathname allowlists.
 *
 * Every field is anonymous, coarse and non-financial.  This file is
 * intentionally minimal; expanding the allowlist requires an explicit
 * privacy review.  See `/docs/posthog-launch-readiness.md`.
 */

/**
 * The canonical **launch-funnel** event vocabulary.  Native Web
 * Analytics uses `$pageview` and `$pageleave`; everything else is a
 * single discrete funnel event.
 *
 * Deliberately absent (gated on an authoritative backend signal that
 * does not yet exist in this repository):
 *
 *   * `subscription_activated` — requires a backend-confirmed paid
 *     entitlement signal; must NOT be fired from a frontend redirect,
 *     URL parameter or optimistic state.
 *   * `plan_upgraded` — requires a backend-confirmed transition from
 *     a lower paid tier to a higher paid tier.
 */
export const ALLOWED_EVENTS = new Set([
  // Native Web Analytics (manual, not autocapture).
  "$pageview",
  "$pageleave",

  // Launch funnel.
  "signup_started",
  "signup_completed",
  "analysis_completed",      // activation event
  "pricing_viewed",
  "plan_cta_clicked",
  "checkout_started",
  "checkout_returned",

  // Supporting coarse product telemetry (unchanged from WS-4 scope).
  "stock_search_submitted",
  "search_result_clicked",
  "analysis_started",
  "locked_feature_viewed",
  "kpi_tooltip_opened",
  "billing_interval_changed",
  "watchlist_item_added",
  "watchlist_item_removed",
  "portfolio_viewed",
  "note_feature_used",
  "label_feature_used",

  // Early Access / Waitlist v1 — privacy-safe funnel.  Each event
  // carries only coarse, non-PII properties.  The waitlist signup
  // email, token and position are NEVER sent to PostHog.
  "early_access_viewed",
  "early_access_signup_submitted",
  "early_access_doi_confirmed",
]);

/**
 * The exhaustive property allowlist.  Any key not listed here is
 * dropped by `_beforeSend` and by `capture()` before dispatch.
 */
export const ALLOWED_PROPERTIES = new Set([
  "app_environment",           // one of: production|staging|preview|development|test
  "locale",                    // "de" | "en"
  "page_group",                // enum from ROUTE_TO_PAGE_GROUP
  "$pathname",                 // bounded canonical pathname (see below)
  "source",                    // short enum
  "authenticated",             // boolean
  "plan",                      // "pro" | "premium"
  "billing_interval",          // "monthly" | "yearly"
  "feature_key",               // fixed allowlist
  "kpi_key",                   // fixed allowlist
  "action",                    // fixed allowlist
  "result_status",             // "ok" | "error" | "cancelled" | "unknown"
  "result_bucket",             // "0" | "1-3" | "4-10" | "11+"
  "device_category",           // provided by PostHog after consent; coarse
  "referring_domain",          // hostname only, never a full URL

  // Privacy-safe acquisition attribution — attached only to the
  // first eligible $pageview of a browser session.  Values are
  // sanitized, lowercased, bounded to 64 chars, and rejected if
  // they contain anything outside [A-Za-z0-9._-].
  "utm_source",
  "utm_medium",
  "utm_campaign",
]);

/**
 * The exhaustive canonical-pathname allowlist.  `canonicalPathname()`
 * in `analytics.js` maps every SPA route to one of these strings;
 * `_beforeSend` additionally validates against this set as a hard
 * backstop.  No stock tickers, no reset tokens, no account/user IDs,
 * no portfolio IDs, no query strings, no fragments ever reach
 * PostHog.
 */
export const ALLOWED_CANONICAL_PATHNAMES = new Set([
  "/",
  "/pricing",
  "/portfolio",
  "/watchlist",
  "/analysis",
  "/authentication",
  "/account",
  "/legal",
  "/early-access",
  "/other",
]);

/**
 * Ordered longest-prefix-first.  Each row is `[spaPrefix,
 * canonicalPathname]`.
 */
export const ROUTE_TO_PAGE_GROUP = [
  ["/pricing", "/pricing"],
  ["/portfolio", "/portfolio"],
  ["/watchlist", "/watchlist"],
  ["/analysis", "/analysis"],
  ["/analyze", "/analysis"],
  ["/auth", "/authentication"],
  ["/login", "/authentication"],
  ["/signup", "/authentication"],
  ["/forgot-password", "/authentication"],
  // Early Access / Waitlist v1 — public page, no PII in the URL.
  // ``/waitlist`` is a 301-style client redirect to ``/early-access``;
  // listing it here classifies any premature read correctly.
  ["/early-access", "/early-access"],
  ["/waitlist", "/early-access"],
  // /reset-password is intentionally classified so a sanitized
  // $pageview may still fire.  The reset token lives ONLY in the URL
  // fragment and is stripped from window.location by the
  // ResetPassword component before any location read.
  ["/reset-password", "/authentication"],
  ["/account", "/account"],
  ["/de/impressum", "/legal"],
  ["/de/datenschutz", "/legal"],
  ["/de/agb", "/legal"],
  ["/de/cookies", "/legal"],
  ["/de/disclaimer", "/legal"],
  ["/de/widerruf", "/legal"],
  ["/de/widerrufsformular", "/legal"],
  ["/en/legal-notice", "/legal"],
  ["/en/privacy", "/legal"],
  ["/en/terms", "/legal"],
  ["/en/cookies", "/legal"],
  ["/en/disclaimer", "/legal"],
  ["/en/withdrawal", "/legal"],
  ["/en/withdrawal-form", "/legal"],
  ["/legal", "/legal"],
  ["/", "/"],
];

// PostHog's own localStorage keys use these prefixes; only these are
// cleared on withdrawal.
export const POSTHOG_LOCALSTORAGE_KEY_PREFIX = "ph_";
